import React, { useEffect, useImperativeHandle, useRef } from "react";
import { getCookieCsrfHeaders } from "../auth/idp";

// === TinyMCE (self-hosted) ===
import tinymce from "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom/model";

// плагины (оставь только нужные)
import "tinymce/plugins/autolink";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/image";
import "tinymce/plugins/table";
import "tinymce/plugins/code";
import "tinymce/plugins/autoresize";

// ОБЯЗАТЕЛЬНО: подключаем стили в бандл,
// и отключим автозагрузку skin/content_css через опции TinyMCE
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/content/default/content.min.css";

export type TinyMCEHandle = { focus: () => void; getEditor: () => any | null };

type Props = {
    value: string;
    onChange: (html: string) => void;
    disabled?: boolean;
    className?: string;
    uploadUrl?: string;   // default: /api/uploads
    uploadField?: string; // default: file
    minHeight?: number;   // default: 800
    maxHeight?: number;   // default: 1400 (убери, если без верхней границы)
};

const TinyMCEEditor = React.forwardRef<TinyMCEHandle, Props>(
    (
        {
            value,
            onChange,
            disabled,
            className,
            uploadUrl = "/api/uploads",
            uploadField = "file",
            minHeight = 800,
            maxHeight = 1400,
        },
        ref
    ) => {
        const hostRef = useRef<HTMLTextAreaElement | null>(null);
        const editorRef = useRef<any | null>(null);
        const lastValRef = useRef<string>(value ?? "");

        useImperativeHandle(ref, () => ({
            focus: () => editorRef.current?.focus?.(),
            getEditor: () => editorRef.current,
        }));

        useEffect(() => {
            let destroyed = false;
            if (!hostRef.current) return;

            tinymce.init({
                target: hostRef.current,

                // ВАЖНО: мы уже импортировали CSS, поэтому запрещаем TinyMCE их подгружать
                skin: false,
                content_css: false,

                menubar: false,
                branding: false,
                statusbar: false,
                license_key: 'gpl',
                plugins: "autoresize autolink link lists image table code",
                toolbar:
                    "undo redo | blocks | bold italic underline | bullist numlist | link image table | code",

                autoresize_on_init: true,
                autoresize_min_height: Math.max(200, minHeight),
                ...(typeof maxHeight === "number" ? { autoresize_max_height: maxHeight } : {}),
                autoresize_bottom_margin: 16,
                autoresize_overflow_padding: 16,

                // свой inline-стиль для контента (не обязателен)
                content_style:
                    "body { font-family: -apple-system, system-ui, Segoe UI, Roboto, Arial, sans-serif; font-size: 14px; }",

                images_upload_handler: async (blobInfo: any) => {
                    const file = blobInfo.blob();
                    const form = new FormData();
                    form.append(uploadField, file, file.name);
                    const res = await fetch(uploadUrl, {
                        method: "POST",
                        body: form,
                        credentials: "include",
                        headers: getCookieCsrfHeaders("POST"),
                    });
                    if (!res.ok) {
                        let msg = `HTTP ${res.status} ${res.statusText}`;
                        try { const j = await res.json(); msg += `: ${JSON.stringify(j)}`; } catch {}
                        throw new Error(msg);
                    }
                    const data = await res.json();
                    const url = data?.url || data?.path;
                    if (!url) throw new Error("Сервер не вернул ссылку (url/path)");
                    return url;
                },

                setup: (ed: any) => {
                    ed.on("init", () => {
                        if (destroyed) return;
                        editorRef.current = ed;
                        ed.setContent(value || "");
                        if (disabled) ed.setMode("readonly");
                        setTimeout(() => { try { ed.execCommand?.("mceAutoResize"); } catch {} }, 0);
                    });

                    ed.on("change keyup setcontent", () => {
                        const html = ed.getContent();
                        lastValRef.current = html;
                        onChange(html);
                        try { ed.execCommand?.("mceAutoResize"); } catch {}
                    });
                },
            });

            return () => {
                destroyed = true;
                if (editorRef.current) {
                    try { editorRef.current.remove?.(); } catch {}
                }
                tinymce.remove(hostRef.current as any);
                editorRef.current = null;
            };
        }, []);

        useEffect(() => {
            const ed = editorRef.current;
            if (!ed) return;
            if ((value ?? "") !== lastValRef.current) {
                lastValRef.current = value ?? "";
                ed.setContent(value ?? "");
                try { ed.execCommand?.("mceAutoResize"); } catch {}
            }
        }, [value]);

        useEffect(() => {
            const ed = editorRef.current;
            if (!ed) return;
            ed.setMode(disabled ? "readonly" : "design");
        }, [disabled]);

        return <textarea ref={hostRef} className={className} />;
    }
);

export default TinyMCEEditor;
